import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Calculator } from "lucide-react";
import { coreServices } from "@/data/services";

/**
 * Horizontally-scrollable service pills + interactive preview panel.
 * Selecting a pill smoothly updates the panel below without a page reload.
 */
export default function ServicePills() {
  const items = coreServices();
  const [active, setActive] = useState(items[0]?.slug || "");
  const activeService = items.find((s) => s.slug === active) || items[0];
  if (!activeService) return null;
  const Icon = activeService.icon;

  const onKey = (e, slug) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setActive(slug);
    }
  };

  return (
    <section
      data-testid="service-pills"
      className="py-14 md:py-20 bg-brand-50/40 border-y border-stone-100"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.25em] text-brand-700 mb-3">
              Explore services
            </div>
            <h2
              className="text-3xl md:text-4xl font-extrabold text-stone-900 tracking-tight leading-tight"
              style={{ fontFamily: "Outfit" }}
            >
              Tap a service to preview it.
            </h2>
          </div>
        </div>

        {/* Pill row */}
        <div
          className="relative -mx-6 md:mx-0 overflow-x-auto scrollbar-hide"
          role="tablist"
          aria-label="Services"
        >
          <div className="flex gap-2 md:gap-3 px-6 md:px-0 pb-2">
            {items.map((s) => {
              const isActive = s.slug === activeService.slug;
              const PillIcon = s.icon;
              return (
                <button
                  key={s.slug}
                  role="tab"
                  aria-selected={isActive}
                  tabIndex={isActive ? 0 : -1}
                  onKeyDown={(e) => onKey(e, s.slug)}
                  onClick={() => setActive(s.slug)}
                  data-testid={`pill-${s.slug}`}
                  className={`inline-flex items-center gap-2 rounded-full px-4 md:px-5 py-2.5 whitespace-nowrap font-bold text-sm transition-all border ${
                    isActive
                      ? "bg-electric-500 text-white border-electric-500 shadow-lg shadow-electric-500/30"
                      : "bg-white text-stone-700 border-stone-200 hover:border-electric-500 hover:text-brand-700"
                  }`}
                >
                  <PillIcon className="w-4 h-4" />
                  {s.title}
                </button>
              );
            })}
          </div>
        </div>

        {/* Preview panel */}
        <div
          key={activeService.slug}
          data-testid="service-preview-panel"
          className="mt-8 rounded-3xl bg-white border border-stone-200 shadow-sm overflow-hidden grid grid-cols-1 md:grid-cols-[1fr_1fr] transition-all animate-fade-up"
        >
          <div className="p-8 md:p-10">
            <div className="w-14 h-14 rounded-2xl bg-brand-100 flex items-center justify-center">
              <Icon className="w-7 h-7 text-brand-700" />
            </div>
            <h3
              className="mt-5 text-2xl md:text-3xl font-extrabold text-stone-900"
              style={{ fontFamily: "Outfit" }}
              data-testid="preview-title"
            >
              {activeService.seoTitle || activeService.title}
            </h3>
            <div className="mt-2 flex items-center gap-3 text-sm">
              <span className="font-bold text-electric-500">From ${activeService.priceFrom}</span>
              {activeService.priceDetail && (
                <span className="text-stone-500">· {activeService.priceDetail}</span>
              )}
            </div>
            <p className="mt-4 text-stone-600 leading-relaxed">{activeService.shortDesc}</p>
            {activeService.idealFor && activeService.idealFor.length > 0 && (
              <ul className="mt-5 space-y-2 text-sm text-stone-700">
                {activeService.idealFor.slice(0, 3).map((point) => (
                  <li key={point} className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-electric-500 mt-2 flex-shrink-0" />
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
            )}
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to={`/${activeService.seoSlug}/`}
                data-testid="preview-learn-more"
                className="inline-flex items-center gap-2 rounded-full bg-stone-900 hover:bg-brand-700 text-white font-bold px-5 py-2.5 text-sm transition-colors"
              >
                Learn more <ArrowRight className="w-3.5 h-3.5" />
              </Link>
              <Link
                to="/pressure-washing-estimate-calculator/"
                data-testid="preview-estimate"
                className="inline-flex items-center gap-2 rounded-full bg-electric-500 hover:bg-electric-600 text-white font-bold px-5 py-2.5 text-sm shadow-lg transition-colors"
              >
                <Calculator className="w-3.5 h-3.5" /> Get estimate
              </Link>
            </div>
          </div>
          <div className="relative bg-gradient-to-br from-brand-100 via-brand-200/60 to-electric-100 flex items-center justify-center p-10 min-h-[220px] md:min-h-[320px] overflow-hidden">
            {activeService.afterPhoto ? (
              <>
                <img
                  src={activeService.afterPhoto}
                  alt={`After — ${activeService.seoTitle || activeService.title} in ${(activeService.seoSlug || "").includes("goodlettsville") ? "Goodlettsville, TN" : ""}`}
                  data-testid="preview-after-photo"
                  className="absolute inset-0 w-full h-full object-cover"
                  loading="lazy"
                />
                <span className="absolute top-4 right-4 text-[10px] font-bold uppercase tracking-widest rounded px-2 py-1 bg-brand-600 text-white shadow">
                  After
                </span>
              </>
            ) : (
              <div className="text-center text-brand-700">
                <Icon className="w-20 h-20 mx-auto opacity-70" />
                <div className="mt-4 text-[10px] uppercase tracking-widest font-bold opacity-70">
                  [ owner photo of {activeService.title.toLowerCase()} project ]
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
